import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import dailyService from '../services/dailyService';
import {
  createVideoCallSession,
  startVideoCall,
  endVideoCall,
  getVideoCallSession,
} from '../services/videoCallService';
import { VideoCallStatus } from '../types/video';
import './Pages.css';
import { DailyEventObject, DailyParticipant } from '@daily-co/daily-js';

export const VideoCall: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const sessionId = searchParams.get('sessionId');
  const recipientId = searchParams.get('recipientId');
  const recipientName = searchParams.get('recipientName') || 'Usuário';
  const consultationId = searchParams.get('consultationId');

  const [status, setStatus] = useState<VideoCallStatus>('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [remoteParticipantId, setRemoteParticipantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(sessionId);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    initializeCall();
    return () => {
      cleanup();
    };
  }, []);

  const initializeCall = async () => {
    try {
      setLoading(true);
      setError(null);

      let session = null;

      // Se já existe sessionId, buscar a sessão
      if (currentSessionId) {
        session = await getVideoCallSession(currentSessionId);
        if (!session) {
          throw new Error('Sessão de videochamada não encontrada');
        }
      } else {
        // Criar nova sessão
        if (!user?.uid || !recipientId) {
          throw new Error('Dados insuficientes para criar videochamada');
        }

        const userRole = (user as any).role || 'patient';
        const nutritionistId = userRole === 'nutritionist' ? user.uid : recipientId;
        const patientId = userRole === 'patient' ? user.uid : recipientId;

        session = await createVideoCallSession(
          nutritionistId,
          patientId,
          consultationId || undefined
        );
        setCurrentSessionId(session.id);
      }

      // Iniciar chamada
      if (!user?.uid) {
        throw new Error('Usuário não autenticado');
      }

      const userRole = (user as any).role || 'patient';
      const role = userRole === 'nutritionist' ? 'nutritionist' : 'patient';
      await startVideoCall(session.id, user.uid, role);

      // Inicializar Daily.co
      await dailyService.initialize();

      // Configurar handlers de eventos
      dailyService.on('joined-meeting', (event) => {
        console.log('Entrou na chamada:', event);
        setStatus('connected');
        setLoading(false);
        updateLocalVideo();
      });

      dailyService.on('participant-joined', (event) => {
        console.log('Participante entrou:', event);
        if (event?.participant && !event.participant.local) {
          setRemoteParticipantId(event.participant.session_id);
          updateRemoteVideo(event.participant.session_id);
        }
      });

      dailyService.on('participant-updated', (event) => {
        const participant = event?.participant;
        if (participant) {
          if (participant.local) {
            updateLocalVideo();
          } else {
            updateRemoteVideo(participant.session_id);
          }
        }
      });

      dailyService.on('participant-left', (event) => {
        console.log('Participante saiu:', event);
        setRemoteParticipantId(null);
        alert('A outra pessoa saiu da chamada.');
        navigate(-1);
      });

      dailyService.on('error', (event) => {
        console.error('Erro no Daily.co:', event);
        setError(`Erro: ${event?.errorMsg || 'Desconhecido'}`);
        setLoading(false);
      });

      // Entrar na chamada
      if (session.dailyRoomUrl) {
        await dailyService.joinCall(session.dailyRoomUrl, user.displayName || 'Usuário');
        setStatus('connecting');
      } else {
        throw new Error('URL da sala não encontrada');
      }

    } catch (error: any) {
      console.error('Erro ao inicializar chamada:', error);
      setError(error.message || 'Erro ao iniciar videochamada');
      setLoading(false);
    }
  };

  const updateLocalVideo = () => {
    const participants = dailyService.participants();
    const localParticipant = participants.local;
    if (localParticipant && localVideoRef.current) {
      const videoTrack = localParticipant.tracks.video;
      if (videoTrack.state === 'playable' && videoTrack.persistentTrack) {
        localVideoRef.current.srcObject = new MediaStream([videoTrack.persistentTrack]);
      } else {
        localVideoRef.current.srcObject = null;
      }
    }
  };

  const updateRemoteVideo = (participantId: string) => {
    const participants = dailyService.participants();
    const remoteParticipant = participants[participantId];
    if (remoteParticipant && remoteVideoRef.current) {
      const videoTrack = remoteParticipant.tracks.video;
      const audioTrack = remoteParticipant.tracks.audio;

      const tracks = [];
      if (videoTrack.state === 'playable' && videoTrack.persistentTrack) {
        tracks.push(videoTrack.persistentTrack);
      }
      if (audioTrack.state === 'playable' && audioTrack.persistentTrack) {
        tracks.push(audioTrack.persistentTrack);
      }

      if (tracks.length > 0) {
        remoteVideoRef.current.srcObject = new MediaStream(tracks);
      } else {
        remoteVideoRef.current.srcObject = null;
      }
    }
  };

  const cleanup = async () => {
    try {
      if (currentSessionId) {
        await endVideoCall(currentSessionId);
      }
      await dailyService.leaveCall();
      await dailyService.destroy();
    } catch (error) {
      console.error('Erro ao limpar recursos:', error);
    }
  };

  const handleToggleMute = async () => {
    try {
      await dailyService.toggleAudio(!isMuted);
      setIsMuted(!isMuted);
    } catch (error) {
      console.error('Erro ao alternar áudio:', error);
    }
  };

  const handleToggleVideo = async () => {
    try {
      await dailyService.toggleVideo(!isVideoEnabled);
      setIsVideoEnabled(!isVideoEnabled);
    } catch (error) {
      console.error('Erro ao alternar vídeo:', error);
    }
  };

  const handleSwitchCamera = async () => {
    // Daily.co web não tem switchCamera direto como no mobile, mas pode-se iterar devices
    // Por simplificação, vamos omitir ou implementar cycleCamera se disponível no service
    try {
      // await dailyService.switchCamera(); // Implementar se necessário
      console.log('Troca de câmera não implementada na web por padrão');
    } catch (error) {
      console.error('Erro ao trocar câmera:', error);
    }
  };

  const handleEndCall = async () => {
    if (window.confirm('Deseja encerrar a videochamada?')) {
      await cleanup();
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className="video-call-container">
        <div className="video-call-loading">
          <div className="spinner"></div>
          <p>Conectando...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-call-container">
        <div className="video-call-error">
          <p>{error}</p>
          <button onClick={initializeCall}>Tentar Novamente</button>
          <button onClick={() => navigate(-1)}>Voltar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="video-call-container">
      {/* Vídeo remoto (tela principal) */}
      <div className="video-call-remote">
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="video-element"
        />
        {!remoteParticipantId && (
          <div className="video-call-placeholder">
            <div className="video-call-placeholder-icon">👤</div>
            <p>{recipientName}</p>
          </div>
        )}
      </div>

      {/* Vídeo local (picture-in-picture) */}
      <div className="video-call-local">
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className="video-element"
        />
        {!isVideoEnabled && (
          <div className="video-call-placeholder-small">
            <div className="video-call-placeholder-icon">👤</div>
          </div>
        )}
      </div>

      {/* Controles */}
      <div className="video-call-controls">
        <button
          className={`video-call-control-btn ${isMuted ? 'disabled' : ''}`}
          onClick={handleToggleMute}
          title={isMuted ? 'Desmutar' : 'Mutear'}
        >
          {isMuted ? '🔇' : '🎤'}
        </button>

        <button
          className={`video-call-control-btn ${!isVideoEnabled ? 'disabled' : ''}`}
          onClick={handleToggleVideo}
          title={isVideoEnabled ? 'Desligar vídeo' : 'Ligar vídeo'}
        >
          {isVideoEnabled ? '📹' : '📹❌'}
        </button>

        <button
          className="video-call-control-btn end-call"
          onClick={handleEndCall}
          title="Encerrar chamada"
        >
          📞❌
        </button>
      </div>

      {/* Status */}
      <div className="video-call-status">
        {status === 'connecting' && <p>Conectando...</p>}
        {status === 'connected' && <p>Conectado</p>}
      </div>
    </div>
  );
};
